import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { BrandLoader } from '@/shared/ui/BrandLoader';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

import type { ClassifyRunState, ProfileRunState } from '../model/sourceDatasets';

/* Profiling or classifying many of a source's tables: what will happen, then each table's progress,
 * then the outcome — the design's plan → run → done.
 *
 * **It can be hidden and keeps going.** A profile run lives in the source's queue, so hiding the
 * dialog — or leaving the page — loses nothing; the page shows a progress band while it runs. A
 * classification run lives in the page, five requests at a time, and the band shows it too.
 */

export interface BulkRun {
  readonly kind: 'profile' | 'classify';
  /** The whole source, rather than a selection — a profile then asks the server for "all". */
  readonly all: boolean;
  readonly ids: readonly string[];
  readonly names: ReadonlyMap<string, string>;
  readonly stage: 'plan' | 'run';
  readonly hidden: boolean;
  readonly startedAt: string | null;
  /** How many a profile request queued; smaller than `ids` when a table was already running. */
  readonly queued: number | null;
  readonly classified: Readonly<Record<string, ClassifyRunState>>;
  /** Each profile job's signature when the run started, so the run can tell its own work from what
   * was already in the queue without reading a server instant against the browser's clock. */
  readonly baseline: Readonly<Record<string, string>>;
  readonly error: string | null;
}

type RunState = ProfileRunState | ClassifyRunState;

const TONE = {
  queued: 'neutral',
  running: 'info',
  retrying: 'warn',
  done: 'ok',
  failed: 'danger',
  review: 'info',
  clean: 'neutral',
} as const satisfies Record<RunState, BadgeTone>;

/** Finished outcomes first in the list would bury what is happening; running ones lead. */
const ORDER: Readonly<Record<RunState, number>> = {
  running: 0,
  retrying: 1,
  queued: 2,
  failed: 3,
  review: 4,
  clean: 5,
  done: 6,
};

export function BulkRunDialog({
  run,
  connectionName,
  states,
  finished,
  busy,
  onStart,
  onHide,
  onClose,
}: {
  readonly run: BulkRun;
  readonly connectionName: string;
  readonly states: Readonly<Record<string, RunState>>;
  readonly finished: boolean;
  readonly busy: boolean;
  readonly onStart: () => void;
  readonly onHide: () => void;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const total = run.ids.length;
  const settled = run.ids.filter((id) => {
    const state = states[id];
    return (
      state === 'done' || state === 'failed' || state === 'review' || state === 'clean'
    );
  }).length;
  const share = total === 0 ? 0 : settled / total;
  const tally = (state: RunState): number =>
    run.ids.filter((id) => states[id] === state).length;
  const items = [...run.ids]
    .map((id) => ({ id, name: run.names.get(id) ?? id, state: states[id] ?? 'queued' }))
    .sort((a, b) => ORDER[a.state] - ORDER[b.state] || a.name.localeCompare(b.name));

  const title = t(`bulkRun.title.${run.kind}`, { count: total });

  return (
    <Modal
      open={!run.hidden}
      title={title}
      hint={connectionName}
      onClose={run.stage === 'run' && !finished ? onHide : onClose}
    >
      {run.stage === 'plan' ? (
        <>
          <dl className="lq-br__facts">
            <div className="lq-br__fact">
              <dt>{t('bulkRun.fact.tables')}</dt>
              <dd>{total.toLocaleString()}</dd>
            </div>
            <div className="lq-br__fact">
              <dt>{t(`bulkRun.fact.${run.kind}.how.label`)}</dt>
              <dd>{t(`bulkRun.fact.${run.kind}.how.value`)}</dd>
            </div>
            <div className="lq-br__fact">
              <dt>{t(`bulkRun.fact.${run.kind}.reads.label`)}</dt>
              <dd>{t(`bulkRun.fact.${run.kind}.reads.value`)}</dd>
            </div>
          </dl>
          <p className="lq-br__cost">{t(`bulkRun.cost.${run.kind}`)}</p>
          {run.error === null ? null : (
            <Alert tone="error" title={t('bulkRun.refused')}>
              {run.error}
            </Alert>
          )}
          <div className="lq-br__actions">
            <Button onClick={onClose}>{t('bulkRun.cancel')}</Button>
            <Button tone="primary" busy={busy} onClick={onStart}>
              {t(`bulkRun.start.${run.kind}`)}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="lq-br__progress">
            {settled < total ? <BrandLoader size="md" /> : null}
            <span
              className="lq-br__track"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={settled}
              aria-label={t('bulkRun.progressLabel')}
            >
              <span
                className="lq-br__fill"
                style={{ transform: `scaleX(${String(share)})` }}
              />
            </span>
            <span className="lq-br__count">
              {t('bulkRun.progress', { done: settled, total })}
            </span>
          </div>
          {run.kind === 'profile' && run.queued !== null && run.queued < total ? (
            <p className="lq-br__note">
              {t('bulkRun.queuedFewer', { queued: run.queued, total })}
            </p>
          ) : null}

          {finished ? (
            <ul className="lq-br__summary">
              {(run.kind === 'profile'
                ? (['done', 'failed'] as const)
                : (['review', 'clean', 'failed'] as const)
              ).map((state) => (
                <li
                  key={state}
                  className="lq-br__sum"
                  data-state={state}
                  /* A zero in the failure colour reads as an alarm about nothing. */
                  data-some={tally(state) > 0 ? 'true' : undefined}
                >
                  <strong className="lq-br__sumValue">
                    {tally(state).toLocaleString()}
                  </strong>
                  {t(`bulkRun.summary.${state}`)}
                </li>
              ))}
            </ul>
          ) : null}

          <ul className="lq-br__items" aria-label={t('bulkRun.itemsLabel')}>
            {items.map((item) => (
              <li key={item.id} className="lq-br__item">
                <span className="lq-br__itemName">{item.name}</span>
                <StatusBadge
                  tone={TONE[item.state]}
                  label={t(`bulkRun.state.${item.state}`)}
                />
              </li>
            ))}
          </ul>

          <div className="lq-br__actions">
            {finished ? (
              <>
                {run.kind === 'classify' && tally('review') > 0 ? (
                  <Link className="lq-btn lq-btn--secondary" to={SCREEN_PATHS.review}>
                    {t('bulkRun.toReview')}
                  </Link>
                ) : null}
                <Button tone="primary" onClick={onClose}>
                  {t('bulkRun.close')}
                </Button>
              </>
            ) : (
              <Button onClick={onHide}>{t('bulkRun.hide')}</Button>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
